import React from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Form, Icon, Popconfirm, Tooltip } from 'antd';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import DataTables from 'components-desktop/DataTables';
import FormDatePicker from 'components-desktop/FormDatePicker';
import FormInput from 'components-desktop/FormInput';
import FormInputNumber from 'components-desktop/FormInputNumber';
import FormSelect from 'components-desktop/FormSelect';
import FormTextArea from 'components-desktop/FormTextArea';
import ChangeHistoriesButton from 'components-desktop/histories/ChangeHistoriesButton';
import helpers from 'misc/helpers';

const propTypes = {
  form: PropTypes.object.isRequired,

  contracts: PropTypes.array.isRequired,
  servicesByCompany: PropTypes.object.isRequired,
};

@autobind
class PatientAuthorizations extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      adding: false,
      editingAuth: null,

      authorizations: [],
      servicesForAdd: [],
      serviceForAdd: null,
      periodForAdd: 'DAY',

      isSaveButtonEnable: true,
    };
  }

  componentDidMount() {
    this.id = this.parseQueryParam('id', this.props.location);
    this.getPatientAuthorizations();
  }

  async getPatientAuthorizations() {
    const params = { patientId: this.id };
    const authorizations = await this.callServiceForRemoteObjects('/authorization/get_list', params);

    this.setState({
      authorizations,
    });
  }

  onAddClick() {
    this.setState({
      adding: true,
      editingAuth: null,
    });
  }

  onEditClick(record) {
    this.setState((prevState, props) => ({
      adding: false,
      editingAuth: prevState.authorizations.find(a => a.getId() === record.id),
    }));
  }

  async onDeleteClick(record) {
    await this.callService('/authorization/delete', { id: record.id });

    this.notifySuccess('Success', 'The authorization record has been deleted.');

    this.getPatientAuthorizations();

    if (this.state.editingAuth && this.state.editingAuth.getId() === record.id) {
      this.onCancelClick();
    }
  }

  onCancelClick() {
    this.setState({
      adding: false,
      editingAuth: null,
      servicesForAdd: [],
      serviceForAdd: null,
      periodForAdd: 'DAY',
    });
  }

  async saveAuth(values) {
    const isDayPeriod = values.period === 'DAY';
    try {
      this.setState({
        isSaveButtonEnable: false,
      });

      if (this.state.adding) {
        const params = {
          patientId: this.id,
          contractId: helpers.extraFormSelectValue(values, 'contractId'),
          serviceId: helpers.extraFormSelectValue(values, 'serviceId'),
          code: helpers.extraFormInputValue(values, 'code'),
          serviceStartDate: helpers.extraFormDateValue(values, 'serviceStartDate'),
          serviceEndDate: helpers.extraFormDateValue(values, 'serviceEndDate'),
          maxUnitForAuth: helpers.extraFormInputNumberValue(values, 'maxUnitForAuth'),
          period: helpers.extraFormSelectValue(values, 'period'),
          remark: helpers.extraFormTextAreaValue(values, 'remark'),

          sunUnit: isDayPeriod ? helpers.extraFormInputNumberValue(values, 'sunUnit') : undefined,
          monUnit: isDayPeriod ? helpers.extraFormInputNumberValue(values, 'monUnit') : undefined,
          tueUnit: isDayPeriod ? helpers.extraFormInputNumberValue(values, 'tueUnit') : undefined,
          wedUnit: isDayPeriod ? helpers.extraFormInputNumberValue(values, 'wedUnit') : undefined,
          thuUnit: isDayPeriod ? helpers.extraFormInputNumberValue(values, 'thuUnit') : undefined,
          friUnit: isDayPeriod ? helpers.extraFormInputNumberValue(values, 'friUnit') : undefined,
          satUnit: isDayPeriod ? helpers.extraFormInputNumberValue(values, 'satUnit') : undefined,

          maxUnitForPeriod: !isDayPeriod
            ? helpers.extraFormInputNumberValue(values, 'maxUnitForPeriod') : undefined,
        };
        await this.callService('/authorization/add', params);
      } else {
        const params = {
          id: this.state.editingAuth.getId(),
          code: helpers.extraFormInputValue(values, 'code'),
          serviceStartDate: helpers.extraFormDateValue(values, 'serviceStartDate'),
          serviceEndDate: helpers.extraFormDateValue(values, 'serviceEndDate'),
          maxUnitForAuth: helpers.extraFormInputNumberValue(values, 'maxUnitForAuth'),
          remark: helpers.extraFormTextAreaValue(values, 'remark'),

          sunUnit: isDayPeriod ? helpers.extraFormInputNumberValue(values, 'sunUnit') : undefined,
          monUnit: isDayPeriod ? helpers.extraFormInputNumberValue(values, 'monUnit') : undefined,
          tueUnit: isDayPeriod ? helpers.extraFormInputNumberValue(values, 'tueUnit') : undefined,
          wedUnit: isDayPeriod ? helpers.extraFormInputNumberValue(values, 'wedUnit') : undefined,
          thuUnit: isDayPeriod ? helpers.extraFormInputNumberValue(values, 'thuUnit') : undefined,
          friUnit: isDayPeriod ? helpers.extraFormInputNumberValue(values, 'friUnit') : undefined,
          satUnit: isDayPeriod ? helpers.extraFormInputNumberValue(values, 'satUnit') : undefined,

          maxUnitForPeriod: !isDayPeriod
            ? helpers.extraFormInputNumberValue(values, 'maxUnitForPeriod') : undefined,
        };
        await this.callService('/authorization/update', params);
      }

      this.notifySuccess('Success', 'The patient authorization has been saved.');

      this.getPatientAuthorizations();
      this.onCancelClick();
    } finally {
      this.setState({
        isSaveButtonEnable: true,
      });
    }
  }

  async onContractSelect(contractId) {
    const { contracts, servicesByCompany } = this.props;

    const contract = contracts.find(c => c.getId() === contractId);
    this.setState({
      servicesForAdd: (contract && servicesByCompany[contract.getLong('companyId')]) || [],
    }, () => {
      this.props.form.setFieldsValue({
        serviceId: contract?.getLong('serviceId'),
      });
    });
  }

  async onServiceSelect(serviceId) {
    if (serviceId) {
      const service =
        await this.callServiceForRemoteObject('/service/get_model', { id: serviceId });

      this.setState({
        serviceForAdd: service,
      });
    } else {
      this.setState({
        serviceForAdd: null,
      });
    }
  }

  async onPeriodSelect(period) {
    this.setState({
      periodForAdd: period,
    });
  }

  validateServiceEndDate(rule, value, callback, source, options) {
    if (value) {
      // Get service start date.
      const serviceStartDate = this.props.form.getFieldValue('serviceStartDate');

      if (serviceStartDate && value.isBefore(serviceStartDate, 'day')) {
        callback('Service end date should not be before service start date.');
      } else {
        callback();
      }
    } else {
      callback();
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.saveAuth(values);
      }
    });
  }

  createColumns() {
    return [{
      title: 'Contract',
      dataIndex: 'companyName',
      sorter: (a, b) => a.companyName?.localeCompare(b.companyName),
    }, {
      title: 'Auth. Code',
      dataIndex: 'code',
      sorter: (a, b) => a.code?.localeCompare(b.code),
    }, {
      title: 'From Date',
      dataIndex: 'serviceStartDateUsString',
      sorter: (a, b) => a.serviceStartDate - b.serviceStartDate,
      defaultSortOrder: 'descend',
    }, {
      title: 'To Date',
      dataIndex: 'serviceEndDateUsString',
      sorter: (a, b) => a.serviceEndDate - b.serviceEndDate,
    }, {
      title: 'Discipline',
      dataIndex: 'discipline',
      sorter: (a, b) => a.discipline?.localeCompare(b.discipline),
    }, {
      title: 'Service Code',
      dataIndex: 'serviceCode',
      sorter: (a, b) => a.serviceCode?.localeCompare(b.serviceCode),
    }, {
      title: 'Max - Auth',
      dataIndex: 'maxUnitForAuth',
      sorter: (a, b) => a.maxUnitForAuth - b.maxUnitForAuth,
    }, {
      title: 'Unit Type',
      dataIndex: 'unitType',
      sorter: (a, b) => a.unitType?.localeCompare(b.unitType),
    }, {
      title: 'Period',
      dataIndex: 'period',
      sorter: (a, b) => a.period?.localeCompare(b.period),
    }, {
      title: 'Max - Period',
      dataIndex: 'maxUnitForPeriod',
      sorter: (a, b) => a.maxUnitForPeriod - b.maxUnitForPeriod,
    }, {
      title: 'Sun',
      dataIndex: 'sunUnit',
    }, {
      title: 'Mon',
      dataIndex: 'monUnit',
    }, {
      title: 'Tue',
      dataIndex: 'tueUnit',
    }, {
      title: 'Wed',
      dataIndex: 'wedUnit',
    }, {
      title: 'Thu',
      dataIndex: 'thuUnit',
    }, {
      title: 'Fri',
      dataIndex: 'friUnit',
    }, {
      title: 'Sat',
      dataIndex: 'satUnit',
    }, {
      title: 'Remark',
      dataIndex: 'remark',
      sorter: (a, b) => a.remark?.localeCompare(b.remark),
      render: (text, record) => (
        <Tooltip title={text}>
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            maxWidth="150px"
            color="textPrimaryDark"
            fontSize="12px"
            ellipsis
            display="inline-block"
          >
            {text}
          </styledComp.Span>
        </Tooltip>
      ),
    }, {
      title: 'Actions',
      render: (text, record) => (
        <FlexRowContainer
          width="match_parent"
          height="wrap_content"
          justifyContent="space-around"
        >
          <styledComp.A
            width="wrap_content"
            height="wrap_content"
            fontSize="14px"
            onClick={() => this.onEditClick(record)}
          >
            Edit
          </styledComp.A>

          <Popconfirm
            title="Are you sure you want to delete this record?"
            placement="top"
            okText="Confirm"
            cancelText="Cancel"
            onConfirm={() => this.onDeleteClick(record)}
          >
            <styledComp.A
              width="wrap_content"
              height="wrap_content"
              fontSize="14px"
            >
              Delete
            </styledComp.A>
          </Popconfirm>
        </FlexRowContainer>
      ),
    }];
  }

  createDataSource(authorizations) {
    const dataSource = [];
    authorizations.forEach((authorization, index) => {
      dataSource.push({
        key: authorization.getId(),
        id: authorization.getId(),
        contractId: authorization.getLong('contractId'),
        companyName: authorization.getString('companyName'),
        code: authorization.getString('code'),
        serviceStartDate: authorization.getLong('serviceStartDate'),
        serviceStartDateUsString: authorization.getString('serviceStartDateUsString'),
        serviceEndDate: authorization.getLong('serviceEndDate'),
        serviceEndDateUsString: authorization.getString('serviceEndDateUsString'),
        discipline: authorization.getRemoteObject('service')?.getString('discipline'),
        serviceCode: authorization.getRemoteObject('service')?.getString('displayCode'),
        maxUnitForAuth: authorization.getDouble('maxUnitForAuth'),
        unitType: this.e2t('serviceUnitType', authorization.getRemoteObject('service')?.getString('unitType')),
        period: this.e2t('authPeriod', authorization.getString('period')),
        maxUnitForPeriod: authorization.getString('period') !== 'DAY' ? authorization.getDouble('maxUnitForPeriod') : null,
        sunUnit: authorization.getString('period') === 'DAY' ? authorization.getDouble('sunUnit') : null,
        monUnit: authorization.getString('period') === 'DAY' ? authorization.getDouble('monUnit') : null,
        tueUnit: authorization.getString('period') === 'DAY' ? authorization.getDouble('tueUnit') : null,
        wedUnit: authorization.getString('period') === 'DAY' ? authorization.getDouble('wedUnit') : null,
        thuUnit: authorization.getString('period') === 'DAY' ? authorization.getDouble('thuUnit') : null,
        friUnit: authorization.getString('period') === 'DAY' ? authorization.getDouble('friUnit') : null,
        satUnit: authorization.getString('period') === 'DAY' ? authorization.getDouble('satUnit') : null,
        remark: authorization.getString('remark'),
      });
    });
    return dataSource;
  }

  renderAddEditForm() {
    const { form, contracts } = this.props;
    const {
      adding,
      editingAuth,
      servicesForAdd,
      serviceForAdd,
      periodForAdd,
      isSaveButtonEnable,
    } = this.state;

    return (
      <FlexColContainer
        width="match_parent"
        height="wrap_content"
        margin="20px 0 8px 0"
        alignItems="center"
        boxShadow="0 1px 6px rgba(0,0,0,.4)"
      >
        <styledComp.AntdForm
          width="match_parent"
          height="wrap_content"
          onSubmit={this.handleSubmit}
        >
          {/* Content */}
          <FlexColContainer
            width="match_parent"
            height="wrap_content"
            padding="20px 20px"
          >
            <styledComp.Span
              width="wrap_content"
              height="wrap_content"
              margin="16px 0 32px 0"
              color="textPrimaryDark"
              fontSize="24px"
              fontWeight="300"
            >
              Add/Edit Authorization Data
            </styledComp.Span>

            <Row type="flex" gutter={24}>
              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormSelect
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="contractId"
                  label="Contract"
                  placeholder="Select..."
                  disabled={!!editingAuth}
                  options={contracts.map(c => ({
                    id: c.getId(),
                    name: c.getString('fullName'),
                  }))}
                  initialValue={editingAuth?.getLong('contractId')}
                  isRequired
                  onSelect={this.onContractSelect}
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                {adding &&
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="serviceId"
                    label="Service Code"
                    placeholder="Select..."
                    options={servicesForAdd.map(s => ({
                      id: s.id,
                      name: s.displayCode,
                    }))}
                    isRequired
                    onSelect={this.onServiceSelect}
                  />
                }

                {editingAuth &&
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="serviceId"
                    label="Service Code"
                    placeholder=""
                    max={120}
                    disabled
                    initialValue={editingAuth?.getRemoteObject('service')?.getString('displayCode')}
                    isRequired
                  />
                }
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormInput
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="discipline"
                  label="Discipline"
                  placeholder=""
                  disabled
                  initialValue={
                    editingAuth?.getRemoteObject('service')?.getString('discipline') ||
                    serviceForAdd?.getString('discipline')
                  }
                  extra="This field is filled automatically."
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormInput
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="unitType"
                  label="Unit Type for Services"
                  placeholder=""
                  disabled
                  initialValue={
                    editingAuth?.getRemoteObject('service')?.getString('unitType') ||
                    serviceForAdd?.getString('unitType')
                  }
                  extra="This field is filled automatically."
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormInput
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="code"
                  label="Authorization Code"
                  placeholder=""
                  initialValue={editingAuth?.getString('code')}
                  isRequired
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormDatePicker
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="serviceStartDate"
                  label="Service Start Date"
                  placeholder="mm/dd/yyyy"
                  extra="Select date from calendar"
                  initialValue={editingAuth?.getMoment('serviceStartDate')}
                  isRequired
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormDatePicker
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="serviceEndDate"
                  label="Service End Date"
                  placeholder="mm/dd/yyyy"
                  extra="Select date from calendar"
                  initialValue={editingAuth?.getMoment('serviceEndDate')}
                  isRequired
                  validator={this.validateServiceEndDate}
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormInputNumber
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="maxUnitForAuth"
                  label="Maximum Units for Authorization"
                  placeholder="0.00"
                  min={0.00}
                  step={0.01}
                  precision={2}
                  extra="In units, enter 0.00 for unlimited."
                  initialValue={editingAuth?.getDouble('maxUnitForAuth') || 0.00}
                  isRequired
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormSelect
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="period"
                  label="Period"
                  placeholder="Select..."
                  disabled={!!editingAuth}
                  extra="Via which authorization is configured."
                  allowClear={false}
                  initialValue={editingAuth?.getString('period') || 'DAY'}
                  options={[
                    { value: 'DAY', text: 'Day' },
                    { value: 'WEEK', text: 'Week' },
                    { value: 'ENTIRE_PERIOD', text: 'Entire Period' },
                  ]}
                  isRequired
                  onSelect={this.onPeriodSelect}
                />
              </Col>

              {((adding && periodForAdd === 'DAY') ||
                (editingAuth && editingAuth.getString('period') === 'DAY')) &&
                <Col xs={{ span: 12 }} sm={{ span: 6 }} md={{ span: 4 }} lg={{ span: 2 }}>
                  <FormInputNumber
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="sunUnit"
                    label="Sun"
                    placeholder=""
                    min={0.00}
                    step={0.01}
                    precision={2}
                    initialValue={editingAuth?.getDouble('sunUnit') || 0.00}
                    isRequired
                  />
                </Col>
              }

              {((adding && periodForAdd === 'DAY') ||
                (editingAuth && editingAuth.getString('period') === 'DAY')) &&
                <Col xs={{ span: 12 }} sm={{ span: 6 }} md={{ span: 4 }} lg={{ span: 2 }}>
                  <FormInputNumber
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="monUnit"
                    label="Mon"
                    placeholder=""
                    min={0.00}
                    step={0.01}
                    precision={2}
                    initialValue={editingAuth?.getDouble('monUnit') || 0.00}
                    isRequired
                  />
                </Col>
              }

              {((adding && periodForAdd === 'DAY') ||
                (editingAuth && editingAuth.getString('period') === 'DAY')) &&
                <Col xs={{ span: 12 }} sm={{ span: 6 }} md={{ span: 4 }} lg={{ span: 2 }}>
                  <FormInputNumber
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="tueUnit"
                    label="Tue"
                    placeholder=""
                    min={0.00}
                    step={0.01}
                    precision={2}
                    initialValue={editingAuth?.getDouble('tueUnit') || 0.00}
                    isRequired
                  />
                </Col>
              }

              {((adding && periodForAdd === 'DAY') ||
                (editingAuth && editingAuth.getString('period') === 'DAY')) &&
                <Col xs={{ span: 12 }} sm={{ span: 6 }} md={{ span: 4 }} lg={{ span: 2 }}>
                  <FormInputNumber
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="wedUnit"
                    label="Wed"
                    placeholder=""
                    min={0.00}
                    step={0.01}
                    precision={2}
                    initialValue={editingAuth?.getDouble('wedUnit') || 0.00}
                    isRequired
                  />
                </Col>
              }

              {((adding && periodForAdd === 'DAY') ||
                (editingAuth && editingAuth.getString('period') === 'DAY')) &&
                <Col xs={{ span: 12 }} sm={{ span: 6 }} md={{ span: 4 }} lg={{ span: 2 }}>
                  <FormInputNumber
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="thuUnit"
                    label="Thu"
                    placeholder=""
                    min={0.00}
                    step={0.01}
                    precision={2}
                    initialValue={editingAuth?.getDouble('thuUnit') || 0.00}
                    isRequired
                  />
                </Col>
              }

              {((adding && periodForAdd === 'DAY') ||
                (editingAuth && editingAuth.getString('period') === 'DAY')) &&
                <Col xs={{ span: 12 }} sm={{ span: 6 }} md={{ span: 4 }} lg={{ span: 2 }}>
                  <FormInputNumber
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="friUnit"
                    label="Fri"
                    placeholder=""
                    min={0.00}
                    step={0.01}
                    precision={2}
                    initialValue={editingAuth?.getDouble('friUnit') || 0.00}
                    isRequired
                  />
                </Col>
              }

              {((adding && periodForAdd === 'DAY') ||
                (editingAuth && editingAuth.getString('period') === 'DAY')) &&
                <Col xs={{ span: 12 }} sm={{ span: 6 }} md={{ span: 4 }} lg={{ span: 2 }}>
                  <FormInputNumber
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="satUnit"
                    label="Sat"
                    placeholder=""
                    min={0.00}
                    step={0.01}
                    precision={2}
                    initialValue={editingAuth?.getDouble('satUnit') || 0.00}
                    isRequired
                  />
                </Col>
              }

              {((adding && periodForAdd !== 'DAY') ||
                (editingAuth && editingAuth.getString('period') !== 'DAY')) &&
                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInputNumber
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="maxUnitForPeriod"
                    label="Maximum Units for Period"
                    placeholder=""
                    min={0.00}
                    step={0.01}
                    precision={2}
                    initialValue={editingAuth?.getDouble('maxUnitForPeriod') || 0.00}
                    isRequired
                  />
                </Col>
              }

              <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                <FormTextArea
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="remark"
                  label="Remark"
                  rows={3}
                  max={2000}
                  extra="For internal use only, will not displayed to the patient."
                  initialValue={editingAuth?.getString('remark')}
                />
              </Col>
            </Row>
          </FlexColContainer>

          {/* Footer */}
          <FlexRowContainer
            width="match_parent"
            height="wrap_content"
            padding="20px 20px"
            justifyContent="center"
            backgroundColor="containerGrayBackground"
          >
            <CustomButton
              width="wrap_content"
              height="32px"
              margin="0 4px 0 0"
              backgroundColor="blue_steel"
              fontSize="14px"
              type="primary"
              loading={!isSaveButtonEnable}
              htmlType="submit"
            >
              SAVE
            </CustomButton>

            <styledComp.AntdButton
              width="wrap_content"
              height="32px"
              fontSize="14px"
              type="default"
              onClick={this.onCancelClick}
            >
              CANCEL
            </styledComp.AntdButton>
          </FlexRowContainer>
        </styledComp.AntdForm>
      </FlexColContainer>
    );
  }

  renderContent() {
    const { ...layoutProps } = this.props;
    const { authorizations, adding, editingAuth } = this.state;

    return (
      <FlexColContainer
        {...layoutProps}
        padding="20px 20px"
      >
        <FlexRowContainer
          width="match_parent"
          height="wrap_content"
          margin="0 0 20px 0"
          justifyContent="space-between"
          alignItems="center"
        >
          <CustomButton
            width="150px"
            height="34px"
            backgroundColor="green_haze"
            fontSize="14px"
            type="primary"
            onClick={this.onAddClick}
          >
            ADD NEW ITEM
            <Icon type="plus" />
          </CustomButton>

          <ChangeHistoriesButton
            width="wrap_content"
            height="wrap_content"
            title="Change History"
            table="Authorization"
            patientId={Number(this.id)}
          />
        </FlexRowContainer>

        <DataTables
          width="match_parent"
          height="wrap_content"
          color="blue_steel"
          fontSize="12px"
          size="small"
          isInsidePanel={false}
          withColumnFilter={false}
          columns={this.createColumns()}
          dataSource={this.createDataSource(authorizations)}
        />

        {(adding || editingAuth) && this.renderAddEditForm()}
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
PatientAuthorizations.propTypes = propTypes;

export default Form.create()(PatientAuthorizations);
